// Git-style line + word diff helper for card descriptions.
// This is intentionally scoped to card description history; AI dialogs and
// other features continue to use `markdown-diff`.

import { diffLines, diffWords } from 'diff';

const ADDED_SENTINEL = '[[SB_LINE_ADDED]]';
const REMOVED_SENTINEL = '[[SB_LINE_REMOVED]]';
const DEFAULT_PREVIEW_LENGTH = 200;
const PREVIEW_CONTEXT_LENGTH = 32;

export type CompactDescriptionDiffSegmentType = 'unchanged' | 'added' | 'removed';

export interface CompactDescriptionDiffSegment {
    type: CompactDescriptionDiffSegmentType;
    text: string;
}

/**
 * Generate a Git-style markdown diff between two description strings.
 *
 * - Uses `diffLines` to compute a line-oriented diff.
 * - Added/removed lines are tagged with a trailing sentinel marker that will be
 *   converted into full-line highlight classes by the markdown viewer.
 * - When a block of lines is changed (removed immediately followed by added),
 *   we show the old line and new line pair, with only the changed segments
 *   wrapped in <del>/<ins> using `diffWords`, and we tag those lines with the
 *   appropriate sentinel markers as well.
 * - Unchanged lines are emitted without extra markup.
 *
 * The returned string is intended to be rendered via TextView, which preserves
 * and styles inline <ins>/<del> tags and uses the sentinels to apply block-level
 * diff styling.
 */
export function generateGitStyleMarkdownDiff(oldText: string, newText: string): string {
    const oldStr = oldText ?? '';
    const newStr = newText ?? '';

    if (!oldStr && !newStr) {
        return 'No content';
    }

    if (oldStr === newStr) {
        return newStr;
    }

    const parts = diffLines(oldStr, newStr);
    const result: string[] = [];

    for (let i = 0; i < parts.length; i++) {
        const part = parts[i];

        // Modified block: removed followed by added
        if (part.removed && i + 1 < parts.length && parts[i + 1].added) {
            const removed = part;
            const added = parts[i + 1];
            i++; // skip the paired added part

            const oldLines = splitIntoLogicalLines(removed.value);
            const newLines = splitIntoLogicalLines(added.value);
            const max = Math.max(oldLines.length, newLines.length);

            for (let lineIdx = 0; lineIdx < max; lineIdx++) {
                const oldLine = oldLines[lineIdx];
                const newLine = newLines[lineIdx];

                if (oldLine === undefined && newLine !== undefined) {
                    // Extra new line in the changed block.
                    // Add a blank line before non-structural lines to ensure markdown
                    // renders them as separate <p> blocks (otherwise they'd merge with
                    // the previous paragraph content).
                    if (!isStructuralLine(newLine)) {
                        result.push('\n');
                    }
                    emitAddedLine(newLine, result);
                    continue;
                }

                if (newLine === undefined && oldLine !== undefined) {
                    // Extra old line in the changed block.
                    // Add a blank line before non-structural lines to ensure markdown
                    // renders them as separate <p> blocks.
                    if (!isStructuralLine(oldLine)) {
                        result.push('\n');
                    }
                    emitRemovedLine(oldLine, result);
                    continue;
                }

                if (oldLine === undefined || newLine === undefined) {
                    continue;
                }

                // Both lines exist: show old + new with word-level highlighting
                const wordDiff = diffWords(oldLine, newLine);
                let oldOut = '';
                let newOut = '';
                let hasChange = false;

                wordDiff.forEach((change: any) => {
                    if (change.added) {
                        hasChange = true;
                        newOut += wrapSegment(change.value, 'ins');
                    } else if (change.removed) {
                        hasChange = true;
                        oldOut += wrapSegment(change.value, 'del');
                    } else {
                        oldOut += change.value;
                        newOut += change.value;
                    }
                });

                // If there is no actual change (diffWords reports all equal), treat
                // this as an unchanged line and emit it once.
                if (!hasChange) {
                    result.push(oldLine + '\n');
                    continue;
                }

                const isStructural = isStructuralLine(oldLine ?? newLine);

                // Emit both old and new lines with line-level markers.
                // For non-heading, non-list lines, insert an extra blank
                // markdown line between them so markdown-it renders separate
                // <p> blocks instead of a single paragraph with <br>.
                emitRemovedLine(oldOut, result);
                if (!isStructural) {
                    result.push('\n');
                }
                emitAddedLine(newOut, result);
            }

            continue;
        }

        // Pure deletions
        if (part.removed) {
            const lines = splitIntoLogicalLines(part.value);
            lines.forEach((line) => emitRemovedLine(line, result));
            continue;
        }

        // Pure insertions
        if (part.added) {
            const lines = splitIntoLogicalLines(part.value);
            lines.forEach((line) => emitAddedLine(line, result));
            continue;
        }

        // Unchanged block
        result.push(part.value);
    }

    return result.join('');
}

export function generateCompactDescriptionDiffPreview(
    oldText: string,
    newText: string,
    maxLength: number = DEFAULT_PREVIEW_LENGTH
): CompactDescriptionDiffSegment[] {
    if (maxLength <= 0) {
        return [];
    }

    const oldStr = oldText ?? '';
    const newStr = newText ?? '';
    const sourceSegments = getFirstChangedSegments(oldStr, newStr, maxLength);
    if (sourceSegments.length === 0) {
        return [];
    }

    return truncatePreviewSegments(normalizePreviewSegments(sourceSegments), maxLength);
}

function getFirstChangedSegments(
    oldText: string,
    newText: string,
    maxLength: number
): CompactDescriptionDiffSegment[] {
    if (oldText === newText) {
        return [];
    }

    const parts = diffLines(oldText, newText);
    for (let i = 0; i < parts.length; i++) {
        const part = parts[i];

        if (part.removed && i + 1 < parts.length && parts[i + 1].added) {
            const changedBlockPreview = buildChangedBlockPreview(part.value, parts[i + 1].value, maxLength);
            if (changedBlockPreview.length > 0) {
                return changedBlockPreview;
            }
            i++;
            continue;
        }

        if (part.removed) {
            if (!hasPreviewText(part.value)) {
                continue;
            }
            return [{ type: 'removed', text: part.value }];
        }

        if (part.added) {
            if (!hasPreviewText(part.value)) {
                continue;
            }
            return [{ type: 'added', text: part.value }];
        }
    }

    return [];
}

function buildChangedBlockPreview(
    oldText: string,
    newText: string,
    maxLength: number
): CompactDescriptionDiffSegment[] {
    const totalTextBudget = maxLength;
    const oldNormalized = normalizePreviewText(oldText);
    const newNormalized = normalizePreviewText(newText);
    if (!oldNormalized.trim() && !newNormalized.trim()) {
        return [];
    }
    if (newNormalized.startsWith(oldNormalized)) {
        return [{ type: 'added', text: focusedPreviewText(newNormalized.slice(oldNormalized.length).trim(), 0, maxLength) }];
    }
    if (oldNormalized.startsWith(newNormalized)) {
        return [{ type: 'removed', text: focusedPreviewText(oldNormalized.slice(newNormalized.length).trim(), 0, maxLength) }];
    }

    const budgets = splitPreviewBudget(oldNormalized.length, newNormalized.length, totalTextBudget);
    const changeOffsets = findFirstChangeOffsets(oldNormalized, newNormalized);

    const segments: CompactDescriptionDiffSegment[] = [
        {
            type: 'removed',
            text: focusedPreviewText(oldNormalized, changeOffsets.oldOffset, budgets.oldBudget),
        },
        {
            type: 'added',
            text: focusedPreviewText(newNormalized, changeOffsets.newOffset, budgets.newBudget),
        },
    ];

    return segments.filter((segment) => segment.text.length > 0);
}

function hasPreviewText(text: string): boolean {
    return normalizePreviewText(text).trim().length > 0;
}

function splitPreviewBudget(
    oldLength: number,
    newLength: number,
    totalBudget: number
): { oldBudget: number; newBudget: number } {
    const firstPassOld = Math.min(oldLength, Math.floor(totalBudget / 2));
    const firstPassNew = Math.min(newLength, totalBudget - firstPassOld);
    const remaining = totalBudget - firstPassOld - firstPassNew;

    if (remaining <= 0) {
        return { oldBudget: firstPassOld, newBudget: firstPassNew };
    }

    const oldExtra = Math.min(oldLength - firstPassOld, remaining);
    return {
        oldBudget: firstPassOld + oldExtra,
        newBudget: Math.min(newLength, firstPassNew + remaining - oldExtra),
    };
}

function findFirstChangeOffsets(oldText: string, newText: string): { oldOffset: number; newOffset: number } {
    let oldOffset = 0;
    let newOffset = 0;

    for (const change of diffWords(oldText, newText) as any[]) {
        if (change.added) {
            return { oldOffset, newOffset };
        }

        if (change.removed) {
            return { oldOffset, newOffset };
        }

        oldOffset += change.value.length;
        newOffset += change.value.length;
    }

    return { oldOffset: 0, newOffset: 0 };
}

function focusedPreviewText(text: string, changeOffset: number, maxLength: number): string {
    if (maxLength <= 0 || !text) {
        return '';
    }

    if (text.length <= maxLength) {
        return text;
    }

    const contextLength = Math.min(PREVIEW_CONTEXT_LENGTH, Math.max(0, Math.floor(maxLength / 3)));
    const start = Math.max(0, changeOffset - contextLength);
    const adjustedStart = Math.min(start, Math.max(0, text.length - maxLength));
    const needsLeadingEllipsis = adjustedStart > 0;
    const leadingLength = needsLeadingEllipsis ? 3 : 0;
    const availableLength = maxLength - leadingLength;
    const needsTrailingEllipsis = adjustedStart + availableLength < text.length;
    const excerptLength = needsTrailingEllipsis ? Math.max(0, availableLength - 3) : availableLength;
    const excerpt = text.slice(adjustedStart, adjustedStart + excerptLength).trim();

    return `${needsLeadingEllipsis ? '...' : ''}${excerpt}${needsTrailingEllipsis ? '...' : ''}`;
}

function normalizePreviewSegments(segments: CompactDescriptionDiffSegment[]): CompactDescriptionDiffSegment[] {
    return segments.reduce<CompactDescriptionDiffSegment[]>((result, segment) => {
        const text = normalizePreviewText(segment.text);
        if (!text) {
            return result;
        }

        const previous = result[result.length - 1];
        if (previous?.type === segment.type) {
            previous.text += text;
            return result;
        }

        result.push({ type: segment.type, text });
        return result;
    }, []).map((segment, index, result) => ({
        type: segment.type,
        text: trimPreviewEdgeWhitespace(segment.text, index === 0, index === result.length - 1),
    })).filter((segment) => segment.text.length > 0);
}

function normalizePreviewText(text: string): string {
    return text.replace(/\s+/g, ' ');
}

function trimPreviewEdgeWhitespace(text: string, isFirst: boolean, isLast: boolean): string {
    let result = text;
    if (isFirst) {
        result = result.trimStart();
    }
    if (isLast) {
        result = result.trimEnd();
    }
    return result;
}

function truncatePreviewSegments(
    segments: CompactDescriptionDiffSegment[],
    maxLength: number
): CompactDescriptionDiffSegment[] {
    const firstChangedIndex = segments.findIndex((segment) => segment.type !== 'unchanged');
    if (firstChangedIndex < 0) {
        return truncateSegments(segments, maxLength);
    }

    const focusedSegments = trimLeadingContext(segments, firstChangedIndex);
    return truncateSegments(focusedSegments, maxLength);
}

function trimLeadingContext(
    segments: CompactDescriptionDiffSegment[],
    firstChangedIndex: number
): CompactDescriptionDiffSegment[] {
    if (firstChangedIndex === 0) {
        return segments;
    }

    const leadingText = segments.slice(0, firstChangedIndex).map((segment) => segment.text).join('');
    if (leadingText.length <= PREVIEW_CONTEXT_LENGTH) {
        return segments;
    }

    return [
        {
            type: 'unchanged',
            text: `...${leadingText.slice(-PREVIEW_CONTEXT_LENGTH).trimStart()}`,
        },
        ...segments.slice(firstChangedIndex),
    ];
}

function truncateSegments(
    segments: CompactDescriptionDiffSegment[],
    maxLength: number
): CompactDescriptionDiffSegment[] {
    const result: CompactDescriptionDiffSegment[] = [];
    let remaining = maxLength;

    for (const segment of segments) {
        if (remaining <= 0) {
            break;
        }

        if (segment.text.length <= remaining) {
            result.push(segment);
            remaining -= segment.text.length;
            continue;
        }

        if (remaining <= 3) {
            result.push({ type: 'unchanged', text: '.'.repeat(remaining) });
            remaining = 0;
            break;
        }

        result.push({
            type: segment.type,
            text: segment.text.slice(0, remaining - 3).trimEnd(),
        });
        result.push({ type: 'unchanged', text: '...' });
        remaining = 0;
    }

    return result.filter((segment) => segment.text.length > 0);
}

function splitIntoLogicalLines(value: string): string[] {
    const lines = value.split('\n');
    // Remove final empty element created by trailing newline, but preserve
    // intentional blank lines (which would contain spaces or other chars).
    if (lines.length > 0 && lines[lines.length - 1] === '') {
        lines.pop();
    }
    return lines;
}

function emitAddedLine(text: string, bucket: string[]): void {
    // Do not mark pure whitespace-only lines; preserve spacing but skip markers.
    if (!text || text.trim() === '') {
        bucket.push('\n');
        return;
    }
    bucket.push(`${text} ${ADDED_SENTINEL}\n`);
}

function emitRemovedLine(text: string, bucket: string[]): void {
    // Do not mark pure whitespace-only lines; preserve spacing but skip markers.
    if (!text || text.trim() === '') {
        bucket.push('\n');
        return;
    }
    bucket.push(`${text} ${REMOVED_SENTINEL}\n`);
}

function wrapSegment(text: string, tag: 'ins' | 'del'): string {
    if (!text) {
        return '';
    }
    return `<${tag}>${text}</${tag}>`;
}

/**
 * Check if a line is a "structural" markdown line that should NOT have a blank
 * line inserted before it (to avoid breaking markdown syntax).
 *
 * Structural lines include: headings, list items, table rows/separators.
 */
function isStructuralLine(line: string | undefined): boolean {
    if (!line) {
        return false;
    }

    const trimmed = line.trimStart();

    // Headings: #, ##, etc.
    if (trimmed.startsWith('#')) {
        return true;
    }

    // Unordered list: *, -, +
    if (trimmed.startsWith('* ') || trimmed.startsWith('- ') || trimmed.startsWith('+ ')) {
        return true;
    }

    // Task list: "* [ ]", "- [ ]", etc.
    if (trimmed.startsWith('* [') || trimmed.startsWith('- [') || trimmed.startsWith('+ [')) {
        return true;
    }

    // Ordered list: "1. ", "2) ", etc.
    if (/^\d+[.)]\s+/.test(trimmed)) {
        return true;
    }

    // Table rows: start with | (e.g., "| cell 1 | cell 2 |")
    // Table separator: "| --- | --- |" or similar
    if (trimmed.startsWith('|')) {
        return true;
    }

    return false;
}
